import type { Cell, ColumnDef, Row, SortingState } from '@tanstack/react-table';

import { Button } from '@connected/ui/components/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@connected/ui/components/table';
import {

    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export type DataTableProps<TData, TValue = unknown> = {
    columns: ColumnDef<TData, TValue>[];
    className?: string;
    cellClassName?: (row: Cell<TData, unknown>) => string;
    rowProps?: (row: Row<TData>) => React.HTMLAttributes<HTMLTableRowElement> & { ref?: React.RefObject<HTMLTableRowElement | null> };
    data: TData[];
    pageSize?: number;
};

export function DataTable<TData, TValue = unknown>({
    className,
    cellClassName,
    columns,
    data,
    pageSize = 10,
    rowProps,
}: DataTableProps<TData, TValue>) {
    const { t } = useTranslation('common');
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
        },
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize } },
    });

    return (
        <div className={cn('rounded-xl border overflow-x-auto w-full')}>
            <Table className={cn('overflow-hidden', className)}>
                <TableHeader className="bg-background-secondary">
                    {table.getHeaderGroups().map(headerGroup => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length
                        ? (
                                table.getRowModel().rows.map(row => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && 'selected'}
                                        {...rowProps?.(row)}
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <TableCell
                                                key={cell.id}
                                                className={cellClassName ? cellClassName(cell) : ''}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )
                        : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-16 py-6 text-center"
                                    >
                                        {t('actions.navigation.no-results')}
                                    </TableCell>
                                </TableRow>
                            )}
                </TableBody>
            </Table>
            {table.getPageCount() > 1 && (
                <div className="pagination flex justify-between items-center p-4">
                    <Button
                        className="w-fit px-4 py-2 bg-primary disabled:opacity-50"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {t('actions.navigation.previous')}
                    </Button>
                    <span>
                        {t('actions.navigation.page')}
                        {' '}
                        <strong>
                            {table.getState().pagination.pageIndex + 1}
                            {' '}
                            van
                            {' '}
                            {table.getPageCount()}
                        </strong>
                    </span>
                    <Button
                        className="w-fit px-4 py-2 bg-primary disabled:opacity-50"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        {t('actions.navigation.next')}
                    </Button>
                </div>
            )}
        </div>
    );
}
